import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createApiClient } from "../api/client";
import PhotoGallery from "../components/PhotoGallery";
import { CurationStepView, MakePublicResult, OrganizerSearchResult, SessionResultsDto } from "../types";
import { isLive, statusBadge } from "../utils/sessionStatus";
import ResultsBody from "../components/ResultsBody";
import RatePlan from "../components/RatePlan";
import Button from "../components/ui/Button";
import CopyButton from "../components/ui/CopyButton";

/**
 * Copies a participant's invite link. "Copy link" reuses the existing link (so
 * previously-shared links keep working); "Reissue" mints a fresh one and kills
 * the old link.
 */
const InviteLinkButton: React.FC<{ sessionId: string; memberId: string }> = ({ sessionId, memberId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [state, setState] = useState<"idle" | "loading" | "copied" | "reissued">("idle");

  const run = async (reissue: boolean) => {
    if (reissue && !window.confirm("Reissue this invite? The current link will stop working.")) return;
    setState("loading");
    try {
      const token = await getAccessTokenSilently();
      const path = `/api/sessions/${sessionId}/participants/${memberId}/invite${reissue ? "/reissue" : ""}`;
      const { data } = await createApiClient(token).post<{ inviteUrl: string }>(path, {});
      await navigator.clipboard.writeText(data.inviteUrl);
      setState(reissue ? "reissued" : "copied");
      setTimeout(() => setState("idle"), 1600);
    } catch {
      setState("idle");
    }
  };

  return (
    <span className="flex items-center gap-2">
      <button
        onClick={() => run(false)}
        className="rounded-full bg-cream-200 px-3 py-1.5 text-xs font-semibold text-ink ring-1 ring-primary-200/70 hover:bg-cream"
      >
        {state === "copied" ? "Copied!" : state === "reissued" ? "Reissued!" : state === "loading" ? "…" : "Copy link"}
      </button>
      <button
        onClick={() => run(true)}
        title="Generate a new link and invalidate the old one"
        className="text-xs font-medium text-ink/45 underline hover:text-ink/70"
      >
        Reissue
      </button>
    </span>
  );
};

const ORG_MOODS: { value: string; label: string; icon: string }[] = [
  { value: "new", label: "Something new", icon: "✨" },
  { value: "reliable", label: "A favorite", icon: "💛" },
  { value: "either", label: "Either", icon: "🤷" },
];

/** Lets the signed-in organizer/member weigh in once during the AI curation phase. */
const OrganizerCuration: React.FC<{ sessionId: string; displayName?: string; onDone: () => void }> = ({
  sessionId,
  displayName,
  onDone,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [step, setStep] = useState<CurationStepView | null>(null);
  const [started, setStarted] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [mood, setMood] = useState<string | null>(null);
  const [specific, setSpecific] = useState("");

  const begin = async () => {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      // Capture intent first so the generated options reflect it.
      await createApiClient(token).post(`/api/sessions/${sessionId}/curation/intake`, {
        mood,
        specific: specific.trim() || null,
      });
      const { data } = await createApiClient(token).post<CurationStepView>(`/api/sessions/${sessionId}/curation`, {
        displayName: displayName ?? null,
      });
      if (data.complete) {
        onDone();
        return;
      }
      setStep(data);
      setStarted(true);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!step?.turn) return;
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<CurationStepView>(
        `/api/sessions/${sessionId}/curation/answer`,
        { turnId: step.turn.turnId, selectedTempIds: selected, sentiment: "Liked" }
      );
      // One-and-done: once weighed in, clear the panel and show results.
      if (data.complete) {
        onDone();
        return;
      }
      setStep(data);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  // "None of these" — ask for a fresh set of options instead of weighing in.
  const more = async () => {
    if (!step?.turn) return;
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<CurationStepView>(
        `/api/sessions/${sessionId}/curation/more`,
        { turnId: step.turn.turnId }
      );
      if (data.complete) {
        onDone();
        return;
      }
      setStep(data);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (!started) {
    return (
      <div className="rounded-3xl border border-primary-100 bg-cream-100 p-4">
        <p className="font-display font-semibold text-ink">Weigh in</p>
        <p className="mt-1 text-sm text-ink/65">What are you up for tonight?</p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {ORG_MOODS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMood(m.value)}
              className={`rounded-xl border px-2 py-2 text-sm font-medium transition-colors ${
                mood === m.value
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-primary-200 bg-white/70 text-ink/70 hover:border-primary-300"
              }`}
            >
              <span className="mr-1">{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>
        <input
          value={specific}
          onChange={(e) => setSpecific(e.target.value)}
          placeholder="Anything specific in mind? (optional)"
          className="mt-2 w-full rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        <Button className="mt-3" onClick={begin} disabled={busy}>
          {busy ? "…" : "Weigh in on tonight"}
        </Button>
      </div>
    );
  }
  if (!step?.turn) return null;
  const turn = step.turn;
  return (
    <div className="rounded-3xl border border-primary-100 bg-white/70 p-4 shadow-warm">
      <p className="font-medium text-ink">{turn.promptText}</p>
      <div className="mt-3 space-y-2">
        {turn.options.map((o) => {
          const on = selected.includes(o.tempId);
          return (
            <button
              key={o.tempId}
              onClick={() => toggle(o.tempId)}
              className={`w-full rounded-2xl border p-3 text-left transition-all ${
                on ? "border-primary-500 bg-primary-50" : "border-primary-100 bg-white/60 hover:border-primary-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold text-ink">{o.title}</span>
                <span className="text-xs uppercase tracking-wide text-primary-400">{o.category}</span>
              </div>
              <p className="mt-1 text-sm text-ink/65">{o.description}</p>
            </button>
          );
        })}
      </div>
      <Button fullWidth className="mt-3" onClick={selected.length > 0 ? submit : more} disabled={busy}>
        {busy ? "Sending…" : selected.length > 0 ? "Send my picks" : "None of these — show more"}
      </Button>
    </div>
  );
};

/** Lets the organizer search the data sources for a place/event and pin it onto the ballot. */
const AddOptionPanel: React.FC<{ sessionId: string; onAdded: (r: SessionResultsDto) => void }> = ({ sessionId, onAdded }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<OrganizerSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).get<OrganizerSearchResult[]>(`/api/sessions/${sessionId}/search`, {
        params: { q: query.trim() },
      });
      setResults(data);
    } finally {
      setSearching(false);
    }
  };

  const add = async (r: OrganizerSearchResult) => {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<SessionResultsDto>(`/api/sessions/${sessionId}/candidates`, r);
      onAdded(data);
      setResults((rs) => rs?.filter((x) => x !== r) ?? null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="mt-4 rounded-3xl border border-primary-100 bg-white/70 p-4 shadow-warm">
      <summary className="cursor-pointer font-medium text-ink">Add an option</summary>
      <p className="mt-1 text-xs text-ink/55">
        Search for a place or event to add to the vote. Added options stay even if you reset &amp; regenerate.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              search();
            }
          }}
          placeholder="e.g. Thai restaurant, mini golf, jazz bar"
          className="w-full rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        <Button onClick={search} disabled={searching || !query.trim()}>
          {searching ? "…" : "Search"}
        </Button>
      </div>
      {results && (
        <ul className="mt-3 space-y-2">
          {results.length === 0 && <li className="text-sm text-ink/50">No matches — try different words.</li>}
          {results.map((r, i) => (
            <li
              key={i}
              className="flex items-start justify-between gap-3 rounded-2xl border border-primary-100 bg-cream-100 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="font-display font-semibold text-ink">{r.title}</p>
                {r.description && <p className="text-xs text-ink/60">{r.description}</p>}
              </div>
              <button
                onClick={() => add(r)}
                disabled={busy}
                className="shrink-0 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
              >
                Add
              </button>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
};

/** Small cycling icon shown inside a button while an action is in flight. */
const ButtonSpinner = () => (
  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden />
);

const PHASES = ["Weigh in", "Vote", "Decided"] as const;

/** Maps a session state to its phase index (0 weigh-in, 1 vote, 2 decided). */
function phaseIndex(state: string): number {
  if (["GatheringParticipants", "IndividualCuration", "ConsensusBuilding"].includes(state)) return 0;
  if (["Narrowing", "Voting", "TieBreak"].includes(state)) return 1;
  return 2;
}

const PhaseStepper: React.FC<{ current: number }> = ({ current }) => (
  <ol className="mt-4 flex items-center gap-1.5 text-xs font-medium">
    {PHASES.map((label, i) => (
      <li key={label} className="flex items-center gap-1.5">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] ${
            i < current
              ? "bg-primary-500 text-white"
              : i === current
              ? "bg-primary-100 text-primary-700 ring-2 ring-primary-400"
              : "bg-cream-200 text-ink/40"
          }`}
        >
          {i < current ? "✓" : i + 1}
        </span>
        <span className={i === current ? "text-ink" : "text-ink/45"}>{label}</span>
        {i < PHASES.length - 1 && <span className="mx-1 h-px w-4 bg-primary-200" />}
      </li>
    ))}
  </ol>
);

const SessionResultsPage: React.FC = () => {
  const { sessionId = "" } = useParams();
  const { getAccessTokenSilently, user } = useAuth0();
  const [results, setResults] = useState<SessionResultsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const { data } = await createApiClient(token).get<SessionResultsDto>(`/api/sessions/${sessionId}/results`);
    setResults(data);
  }, [getAccessTokenSilently, sessionId]);

  const getPhotoClient = useMemo(
    () => async () => createApiClient(await getAccessTokenSilently()),
    [getAccessTokenSilently]
  );

  // Reset to a clean loading state when switching plans, so we never show stale data.
  useEffect(() => {
    setResults(null);
    setNotFound(false);
    setLoading(true);
  }, [sessionId]);

  useEffect(() => {
    load()
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [load]);

  // Live-poll while the plan is still in progress.
  useEffect(() => {
    if (!results || !isLive(results.state)) return;
    const id = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(id);
  }, [results?.state, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when returning to the tab/window, so navigating back in isn't stale.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") load().catch(() => {});
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const togglePublic = async (next: boolean) => {
    const token = await getAccessTokenSilently();
    const { data } = await createApiClient(token).put<MakePublicResult>(
      `/api/sessions/${sessionId}/public`,
      { isPublic: next }
    );
    setResults((r) => (r ? { ...r, resultsPublic: data.resultsPublic, publicUrl: data.publicUrl } : r));
  };

  const castVote = async (suggestionId: string) => {
    const token = await getAccessTokenSilently();
    const { data } = await createApiClient(token).post<SessionResultsDto>(
      `/api/sessions/${sessionId}/vote`,
      { suggestionId, displayName: user?.name ?? user?.email ?? null }
    );
    setResults(data);
  };

  const advance = async () => {
    const voting = results?.state === "Voting";
    const msg = voting
      ? "Decide now with the votes cast so far?"
      : "Move ahead with everyone who's weighed in so far?";
    if (!window.confirm(msg)) return;
    setAdvancing(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<SessionResultsDto>(`/api/sessions/${sessionId}/advance`, {});
      setResults(data);
    } finally {
      setAdvancing(false);
    }
  };

  const cancelPlan = async () => {
    if (!window.confirm("Cancel this plan?")) return;
    const token = await getAccessTokenSilently();
    await createApiClient(token).post(`/api/sessions/${sessionId}/cancel`, {});
    load();
  };

  const removeCandidate = async (suggestionId: string) => {
    const token = await getAccessTokenSilently();
    const { data } = await createApiClient(token).delete<SessionResultsDto>(
      `/api/sessions/${sessionId}/candidates/${suggestionId}`
    );
    setResults(data);
  };

  const rebuild = async () => {
    if (!window.confirm("Reset all votes and regenerate the options for this plan?")) return;
    setAdvancing(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<SessionResultsDto>(`/api/sessions/${sessionId}/rebuild`, {});
      setResults(data);
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    );
  }

  if (notFound || !results) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <h1 className="font-display text-2xl font-semibold text-ink">Plan not found</h1>
        <p className="mt-2 text-ink/65">It may have been removed, or you don't have access.</p>
        <Link to="/dashboard" className="mt-4 inline-block font-medium text-primary-600">
          ‹ Back to families
        </Link>
      </div>
    );
  }

  const badge = statusBadge(results.state);

  return (
    <div className="mx-auto max-w-2xl">
      <Link to={`/families/${results.familyId}/plans`} className="text-sm text-ink/55 hover:text-ink">
        ‹ {results.familyName}
      </Link>

      <div className="mt-2 flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-semibold text-ink">Tonight's plan</h1>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
      </div>

      {results.state !== "Cancelled" && <PhaseStepper current={phaseIndex(results.state)} />}

      {/* Controls */}
      <div className="mt-5 rounded-3xl border border-primary-100 bg-white/70 p-4 shadow-warm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-ink">Public results</p>
            <p className="text-xs text-ink/55">
              {results.resultsPublic ? "Anyone with the link can view the live results." : "Off — results are private."}
            </p>
          </div>
          {results.resultsPublic ? (
            <Button variant="secondary" onClick={() => togglePublic(false)}>
              Make private
            </Button>
          ) : (
            <Button onClick={() => togglePublic(true)}>Make public</Button>
          )}
        </div>
        {results.resultsPublic && results.publicUrl && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-cream-100 px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-sm text-ink/70">{results.publicUrl}</span>
            <CopyButton text={results.publicUrl} />
          </div>
        )}
      </div>

      {/* Re-issue invite links (the original links aren't recoverable). */}
      {results.participants.length > 0 && (
        <details className="mt-4 rounded-3xl border border-primary-100 bg-white/70 p-4 shadow-warm">
          <summary className="cursor-pointer font-medium text-ink">Invite links</summary>
          <p className="mt-1 text-xs text-ink/55">
            Copy a member's link to share — the same link keeps working (good for 12 hours). Reissue only if
            you need to invalidate the old one.
          </p>
          <ul className="mt-3 space-y-2">
            {results.participants.map((p) => (
              <li key={p.memberId} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-ink/80">{p.displayName}</span>
                <InviteLinkButton sessionId={sessionId} memberId={p.memberId} />
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Phase 1 — weigh in. Once done it locks to a confirmation; it never re-opens. */}
      {["GatheringParticipants", "IndividualCuration", "ConsensusBuilding"].includes(results.state) &&
        (results.youAreParticipant && results.yourCurationComplete ? (
          <div className="mt-6 rounded-3xl border border-primary-100 bg-cream-100 p-4 text-sm text-ink/70">
            ✓ You've weighed in. Waiting on the family — or use <span className="font-medium text-ink">Move to Voting</span> below to move ahead.
          </div>
        ) : (
          <div className="mt-6">
            <OrganizerCuration
              sessionId={sessionId}
              displayName={user?.name ?? user?.email ?? undefined}
              onDone={load}
            />
          </div>
        ))}

      <div className="mt-6">
        <ResultsBody
          results={results}
          voting={results.state === "Voting"}
          onVote={castVote}
          onRemove={["Voting", "TieBreak"].includes(results.state) ? removeCandidate : undefined}
        />
      </div>

      {["Voting", "TieBreak"].includes(results.state) && (
        <AddOptionPanel sessionId={sessionId} onAdded={setResults} />
      )}

      {results.decision && (
        <RatePlan
          startsAt={results.decision.startsAt}
          yourRating={results.yourRating}
          average={results.averageRating}
          count={results.ratingCount}
          onRate={async (stars, note) => {
            const token = await getAccessTokenSilently();
            const { data } = await createApiClient(token).post<SessionResultsDto>(
              `/api/sessions/${sessionId}/rating`,
              { stars, note }
            );
            setResults(data);
          }}
        />
      )}

      {results.decision && <PhotoGallery getClient={getPhotoClient} basePath={`/api/sessions/${sessionId}`} />}

      {isLive(results.state) && (
        <div className="mt-8 flex flex-col items-center gap-2">
          {["IndividualCuration", "ConsensusBuilding", "Narrowing"].includes(results.state) && (
            <>
              <Button onClick={advance} disabled={advancing}>
                {advancing && <ButtonSpinner />}
                {advancing ? "Lining up options…" : "Move to Voting"}
              </Button>
              <p className="max-w-sm text-center text-xs text-ink/45">
                {advancing
                  ? "Gathering options from the area — this can take a few seconds."
                  : "Builds the options from whoever's weighed in so far and opens voting — everyone can still vote."}
              </p>
            </>
          )}
          {results.state === "Voting" && (
            <>
              <Button onClick={advance} disabled={advancing}>
                {advancing && <ButtonSpinner />}
                {advancing ? "Deciding…" : "Decide now"}
              </Button>
              <p className="max-w-sm text-center text-xs text-ink/45">
                Locks in the winner from the votes cast so far.
              </p>
            </>
          )}
          <div className="mt-2 flex items-center gap-4">
            {["Voting", "TieBreak"].includes(results.state) && (
              <button
                onClick={rebuild}
                disabled={advancing}
                className="text-sm text-ink/45 underline hover:text-ink/70 disabled:opacity-50"
              >
                Reset &amp; regenerate options
              </button>
            )}
            <button onClick={cancelPlan} className="text-sm text-ink/45 underline hover:text-red-600">
              Cancel this plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionResultsPage;
